/**
 * 套餐数据转换工具函数
 */

import type { PackageRaw, Package, BillingCycle, PackageType } from '@/types/package'

/** 分转元的除数 */
const FEN_TO_YUAN_DIVISOR = 100 as const

/**
 * 将分转换为元
 * @param fen 金额（分）
 * @returns 金额（元）
 */
export function fenToYuan(fen: number): number {
  return fen / FEN_TO_YUAN_DIVISOR
}

/**
 * 根据套餐类型获取计费周期
 * @param packageType 套餐类型
 * @returns 计费周期类型
 */
export function getBillingCycleFromType(packageType: PackageType): BillingCycle {
  switch (packageType) {
    case 'weekly':
      return 'weekly'
    case 'quarterly':
      return 'quarterly'
    case 'yearly':
      return 'yearly'
    case 'monthly':
    case 'client_seat':
    case 'client_plus':
      return 'monthly'
    case 'recharge':
    default:
      return 'onetime'
  }
}

/**
 * 计算折扣百分比
 * @param price 折扣价（分）
 * @param originalPrice 原价（分）
 * @returns 折扣百分比，如 30 表示 30% 折扣；如果不适用则返回 null
 */
export function calculateDiscountPercentage(
  price: number,
  originalPrice: number | null
): number | null {
  if (originalPrice === null || originalPrice <= price) {
    return null
  }
  return Math.round((1 - price / originalPrice) * 100)
}

/**
 * 判断是否为推荐套餐
 * @param sortOrder 排序权重
 * @returns 是否推荐
 */
export function isRecommendedPackage(sortOrder: number): boolean {
  return sortOrder === 1
}

/**
 * 从 description 解析权益列表（按换行分割）
 * @param description 套餐描述
 * @returns 权益列表
 */
export function parseFeatures(description: string | null): readonly string[] {
  if (description === null || description === '') {
    return []
  }
  return description
    .split('\n')
    .map((line: string): string => line.trim())
    .filter((line: string): boolean => line.length > 0)
}

/**
 * 将后端原始套餐数据转换为前端展示格式
 * @param raw 原始套餐数据
 * @returns 转换后的套餐数据
 */
export function transformPackage(raw: PackageRaw): Package {
  const originalPriceYuan = raw.originalPrice !== null
    ? fenToYuan(raw.originalPrice)
    : null

  return {
    id: raw.id,
    name: raw.name,
    description: raw.description ?? '',
    features: parseFeatures(raw.description),
    packageType: raw.packageType,
    priceYuan: fenToYuan(raw.price),
    originalPriceYuan,
    discountPercentage: calculateDiscountPercentage(raw.price, raw.originalPrice),
    totalCredits: raw.balanceAmount + raw.bonusAmount,
    baseCredits: raw.balanceAmount,
    bonusCredits: raw.bonusAmount,
    allowCustomPurchase: raw.allowCustomPurchase,
    customPricePer100: raw.customPricePer100,
    billingCycle: getBillingCycleFromType(raw.packageType),
    sortOrder: raw.sortOrder,
    isRecommended: isRecommendedPackage(raw.sortOrder),
  }
}

/**
 * 批量转换套餐数据
 * @param rawList 原始套餐列表
 * @returns 转换后的套餐列表
 */
export function transformPackageList(rawList: readonly PackageRaw[]): Package[] {
  return rawList.map(transformPackage)
}

/**
 * 按计费周期筛选套餐（兼容旧版）
 * @param packages 套餐列表
 * @param cycle 计费周期
 * @returns 筛选后的套餐列表
 */
export function filterPackagesByBillingCycle(
  packages: readonly Package[],
  cycle: 'monthly' | 'yearly'
): Package[] {
  return packages.filter((pkg: Package): boolean => pkg.billingCycle === cycle)
}

/**
 * 按套餐类型筛选套餐
 * @param packages 套餐列表
 * @param types 套餐类型数组
 * @returns 筛选后的套餐列表
 */
export function filterPackagesByType(
  packages: readonly Package[],
  types: PackageType[]
): Package[] {
  return packages.filter((pkg: Package): boolean => types.includes(pkg.packageType))
}
